import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import { authClient, getAccessToken, signOut } from './auth'
import { logAuthEvent } from './authDiag'

// Resolve the API base URL with a native fallback, mirroring
// resolveGoTrueUrl() in lib/auth.ts. Build-time env var wins; otherwise
// on a native (Capacitor) build we MUST default to the production API
// rather than localhost — the device can't reach http://localhost:8000,
// so a build with no VITE_API_URL (e.g. the Android debug APK built in
// CI without the prod env) surfaced as "Couldn't load your profile" on
// the device even though auth succeeded (GoTrue already had its own
// native fallback; the API client didn't). 2026-06-26 Android bring-up.
// On the web we keep localhost so local dev still talks to the dev API.
function resolveApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_URL
  if (fromEnv) return fromEnv
  if (Capacitor.isNativePlatform()) return 'https://api.trybeup.com'
  return 'http://localhost:8000'
}

const BASE_URL = resolveApiBaseUrl()

/**
 * Base URL for auth-email redirect links (magic link, password reset, signup
 * confirm). On the native app `window.location.origin` is `capacitor://localhost`
 * — which an email client CANNOT open, so the emailed link dead-ends and a
 * passwordless user is stuck at the door. Use the live web URL on native so the
 * tap always lands on the working web flow; on web the current origin (prod /
 * dev / localhost) is already correct.
 */
export function webRedirectBase(): string {
  return Capacitor.isNativePlatform() ? 'https://trybeup.com' : window.location.origin
}

export const api = axios.create({
  baseURL: BASE_URL,
})

api.interceptors.request.use(async (config) => {
  const token = await getFreshAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  ;(config as TimedConfig)._startedAt = performance.now()
  return config
})

// ─── Slow-call reporting (load-time release 4, 2026-10-02) ─────────────────
// The API had no timing record at all. Every call now measures wall time on
// the client and reads the server's own `Server-Timing: app;dur=…` header, so
// a slow call can be split into "server was slow" vs "network was slow".
// Anything over SLOW_API_MS goes to PostHog as `api.slow` (route with ids
// stripped, client ms, server ms, status), capped per session so a bad
// connection can't flood analytics.
type TimedConfig = { _startedAt?: number; url?: string; method?: string }
export const SLOW_API_MS = 1500
const SLOW_REPORT_CAP = 12
let slowReports = 0

/** `/api/habits/3f2a…/complete` → `/api/habits/:id/complete`. */
export function routeTemplate(url: string | undefined): string {
  const path = (url || '').split('?')[0]
  return path.replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, '/:id').replace(/\/\d{4}-\d{2}-\d{2}(?=\/|$)/g, '/:date').replace(/\/\d+(?=\/|$)/g, '/:n')
}

export function serverMsFrom(headerValue: string | undefined | null): number | null {
  if (!headerValue) return null
  const m = /app;dur=([\d.]+)/.exec(headerValue)
  return m ? Math.round(Number(m[1])) : null
}

function reportIfSlow(config: TimedConfig | undefined, headers: Record<string, unknown> | undefined, status: number | undefined) {
  try {
    const started = config?._startedAt
    if (!started) return
    const clientMs = Math.round(performance.now() - started)
    if (clientMs < SLOW_API_MS || slowReports >= SLOW_REPORT_CAP) return
    slowReports += 1
    const serverMs = serverMsFrom((headers?.['server-timing'] as string | undefined) ?? null)
    import('@/hooks/useAnalytics')
      .then(({ trackEvent }) => trackEvent('api.slow', {
        route: routeTemplate(config?.url),
        method: (config?.method || 'get').toUpperCase(),
        clientMs,
        serverMs,
        status: status ?? 0,
      }))
      .catch(() => { /* analytics only */ })
  } catch { /* never let telemetry break a request */ }
}

let redirecting = false
type RefreshOutcome = 'ok' | 'auth-failed' | 'network-failed'
let refreshing: Promise<RefreshOutcome> | null = null

// Proactively refresh an EXPIRED / near-expiry access token BEFORE attaching
// it to an outgoing request, instead of letting the request 401 and refreshing
// reactively. This matters most on a cold start: GoTrue runs with
// autoRefreshToken:false (deliberately — see lib/auth.ts), so after the app has
// been closed past the token lifetime (GOTRUE_JWT_EXP=3600, 1 hr), getSession() hands back a stale
// token. Without this, the first /api/users/me — which gates first paint —
// pays request→401→refresh→retry (3 round-trips). With it, it's refresh→request
// (2). Founder 7s cold-start, 2026-06-14.
//
// Strictly "never worse than today": if the proactive refresh fails (network
// or auth) we fall back to whatever token we have and the response-side 401
// interceptor handles it EXACTLY as before. The `refreshing` singleton in
// tryRefreshSession dedupes concurrent callers (the 15 home-tab queries that
// fire at once all await one refresh). A valid token (the warm/common case)
// skips the refresh entirely — no added latency.
const PROACTIVE_REFRESH_MARGIN_S = 60
async function getFreshAccessToken(): Promise<string | null> {
  const { data } = await authClient.getSession()
  const sess = data.session
  if (!sess?.access_token) return null
  const expiresAt = sess.expires_at
  if (typeof expiresAt === 'number') {
    const secondsLeft = expiresAt - Math.floor(Date.now() / 1000)
    if (secondsLeft < PROACTIVE_REFRESH_MARGIN_S) {
      const outcome = await tryRefreshSession()
      if (outcome === 'ok') {
        const refreshed = await authClient.getSession()
        return refreshed.data.session?.access_token ?? sess.access_token
      }
      // Refresh failed (network/auth) — return the current token and let the
      // response 401 path decide. Identical to pre-change behavior.
    }
  }
  return sess.access_token
}

// Attempt a session refresh on 401. We split the outcome into three
// cases so the caller can react appropriately:
//   - 'ok'             : new access token in hand, retry the request
//   - 'auth-failed'    : refresh token rejected — session is genuinely gone
//   - 'network-failed' : couldn't reach the auth server (offline, DNS, CORS
//                        timeout, etc.) — keep the user signed in and let
//                        them retry; transient blips shouldn't log them out
//                        and lose the workout draft they're editing.
//
// Two robustness layers on top of the raw GoTrue refresh:
//
// (1) PER-ATTEMPT TIMEOUT (REFRESH_TIMEOUT_MS). authClient.refreshSession()
//     uses fetch with no timeout — on a slow / hung GoTrue connection the
//     promise can hang for ~60s (WebView default), during which every
//     queued query waits. The founder report ("when I tap Identity Stage
//     there's a long delay then it logs me out") is exactly that: the
//     ProfilePage mount fires 5+ parallel queries that all wait on this
//     one refresh, and the perception is "the app froze, then it kicked
//     me out". The 10s cap is per-attempt; on timeout we classify as
//     network-failed (NOT auth-failed) so the session survives the slow
//     connection.
//
// (2) RACE-RECOVERY RETRY. When the first attempt returns auth-failed,
//     wait briefly and try once more. Mirrors the warmer pattern in
//     useAuth.ts and was previously ONLY in the warmer — meaning a
//     refresh triggered by an API 401 (ProfilePage opening, push
//     notification deep-link, etc.) got no race recovery, and a single
//     transient 401/403 from GoTrue went straight to signOut. The
//     scenario the retry survives is a cross-context refresh-token
//     rotation race: device/tab A consumes RT1 and writes RT2 to
//     storage; this caller captured RT1 in memory before the write and
//     loses; 1500ms later, the live RT2 is durable in storage and a
//     fresh authClient call picks it up. Only after a CONFIRMED
//     twice-failed refresh do we accept the auth-failed outcome.
const REFRESH_TIMEOUT_MS = 10_000
const RACE_RETRY_DELAY_MS = 1500

interface RefreshAttempt {
  outcome: RefreshOutcome
  /** HTTP status from GoTrue, when the call completed with a structured error. */
  status?: number
  /** Error message from GoTrue, when the call completed with a structured error. */
  message?: string
  /** True when the attempt threw / rejected (catch path). */
  threw?: boolean
  /** True when our 10s per-attempt timeout fired before refreshSession resolved. */
  timedOut?: boolean
}

async function refreshOnce(): Promise<RefreshAttempt> {
  let timer: ReturnType<typeof setTimeout> | null = null
  try {
    const result = await Promise.race([
      authClient.refreshSession(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('refresh_timeout')), REFRESH_TIMEOUT_MS)
      }),
    ])
    const { data, error } = result
    if (!error && data.session?.access_token) {
      return { outcome: 'ok' }
    }
    // GoTrue returns an error object for both auth and network failures.
    // Only 401 / 403 are real auth rejections (refresh token genuinely
    // rejected). Everything else — 5xx, 502/503/504 gateway blips, no
    // status, undefined — is treated as a transient network failure
    // and we keep the session alive. The previous logic flagged ANY
    // non-zero status as auth-failed, which signed users out during
    // brief GoTrue outages or deploy hiccups (the recurring "Google
    // signed me out again" reports).
    const errObj = error as { status?: number; message?: string } | null
    const status = errObj?.status
    const message = errObj?.message
    const outcome: RefreshOutcome = (status === 401 || status === 403) ? 'auth-failed' : 'network-failed'
    return { outcome, status, message }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    // Distinguish our own 10s timeout from any other thrown fetch error —
    // both classify as network-failed for caller behaviour, but the
    // diagnostic event records which one fired so the /login banner can
    // tell "GoTrue was slow" from "fetch threw CORS / DNS / etc.".
    const timedOut = message === 'refresh_timeout'
    return { outcome: 'network-failed', threw: true, message, timedOut }
  } finally {
    if (timer !== null) clearTimeout(timer)
  }
}

export async function tryRefreshSession(): Promise<RefreshOutcome> {
  if (refreshing) return refreshing
  refreshing = (async () => {
    logAuthEvent('refresh_attempt', {})
    const first = await refreshOnce()
    // Pass through status / message / threw / timedOut so the /login
    // diagnostic banner can surface the actual cause of a refresh
    // failure (e.g. 400 invalid_grant vs 401 token rejected vs 10s
    // timeout vs thrown fetch error). Pre-2026-06-19 these fields were
    // logged; the v1.1.315 refactor accidentally dropped them, which
    // erased exactly the signal we need to investigate surprise
    // sign-outs. Restored 2026-06-19 (v1.1.316).
    logAuthEvent('refresh_outcome', stripUndefined(first))
    if (first.outcome !== 'auth-failed') return first.outcome
    // Race recovery — see (2) above.
    logAuthEvent('refresh_retry_attempt', { afterMs: RACE_RETRY_DELAY_MS })
    await new Promise<void>((resolve) => setTimeout(resolve, RACE_RETRY_DELAY_MS))
    const second = await refreshOnce()
    logAuthEvent('refresh_retry_outcome', stripUndefined(second))
    return second.outcome
  })().finally(() => { refreshing = null })
  return refreshing
}

// logAuthEvent serializes its detail via JSON.stringify (see
// authDiag.deepEqualDetail). Undefined values turn into missing keys
// during stringify, which is what we want — but emitting them as
// explicit `undefined` properties means the dedupe-equal check is doing
// extra work and the storage payload is slightly larger. Strip them
// upfront so the log is tight.
function stripUndefined(obj: object): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v
  }
  return out
}

async function handle401(refreshResult?: RefreshOutcome) {
  if (redirecting || window.location.pathname === '/login') return
  const outcome = refreshResult ?? (await tryRefreshSession())
  // Refresh succeeded — caller will retry; nothing to do.
  if (outcome === 'ok') return
  // Network failure during refresh — keep the session and let the caller
  // surface the error. Don't redirect; the user can try again and a future
  // refresh may succeed.
  if (outcome === 'network-failed') return
  redirecting = true
  logAuthEvent('reactive_signout', { reason: 'auth-failed refresh on 401' })
  await signOut('reactive-401').catch(() => {})
  window.location.href = '/login'
  // Reset after a short delay so a failed redirect doesn't permanently block
  setTimeout(() => { redirecting = false }, 5000)
}

/**
 * True when an error is the profile-incomplete gate: HTTP 403 with a
 * structured detail `{code: "profile_incomplete", missing, message}` from
 * backend/dependencies.py. While that state holds, the blocking ProfileGate
 * card is already on screen — so callers that pattern-match on bare 403s
 * (habit limits, AI daily caps, photo quotas, "upgrade to Premium") MUST
 * check this first and stay quiet, or a user who's merely missing a birth
 * year gets told to buy Premium. Found 2026-08-04, the day the gate shipped.
 */
export function isProfileIncompleteError(err: unknown): boolean {
  const anyErr = err as { code?: string; response?: { data?: { detail?: { code?: string } } }; detail?: { code?: string } } | null
  if (!anyErr) return false
  if (anyErr.code === 'profile_incomplete') return true
  const detail = anyErr.response?.data?.detail ?? anyErr.detail
  return !!detail && typeof detail === 'object' && detail.code === 'profile_incomplete'
}

api.interceptors.response.use(
  (res) => {
    reportIfSlow(res.config as TimedConfig, res.headers as Record<string, unknown>, res.status)
    return res
  },
  async (error) => {
    const original = error.config as (typeof error.config & { _retried?: boolean }) | undefined
    reportIfSlow(original as TimedConfig | undefined, error.response?.headers as Record<string, unknown> | undefined, error.response?.status)
    // Profile-incomplete 403s carry an OBJECT detail. Normalise here so the
    // ~165 call sites doing `err.message` render the human sentence instead
    // of "[object Object]" or "Request failed with status code 403".
    const gateDetail = error.response?.status === 403 ? error.response?.data?.detail : undefined
    if (gateDetail && typeof gateDetail === 'object' && gateDetail.code === 'profile_incomplete') {
      error.message = gateDetail.message || 'Please finish setting up your account.'
      ;(error as { code?: string }).code = 'profile_incomplete'
    }
    if (error.response?.status === 401) {
      // A deleted account (tombstone) keeps a valid GoTrue session, so the
      // refresh "succeeds" and the app spun on "Couldn't load your profile"
      // with no way out (review 2026-09-13). Sign out and leave.
      const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail
      if (detail === 'account_deleted' && !redirecting) {
        redirecting = true
        await signOut('account-deleted').catch(() => {})
        window.location.href = '/login'
        return Promise.reject(error)
      }
      // First 401 on a given request: try to refresh and retry once.
      if (original && !original._retried) {
        original._retried = true
        const outcome = await tryRefreshSession()
        if (outcome === 'ok') {
          const token = await getAccessToken()
          if (token) {
            original.headers = { ...(original.headers || {}), Authorization: `Bearer ${token}` }
          }
          return api.request(original)
        }
        await handle401(outcome)
      } else {
        await handle401()
      }
    }
    return Promise.reject(error)
  }
)

/** Field names the user would recognise, for validation messages. */
const FIELD_LABELS: Record<string, string> = {
  weight: 'Weight',
  reps: 'Reps',
  rpe: 'RPE',
  rir: 'RIR',
  duration_seconds: 'Duration',
  durationSeconds: 'Duration',
  duration: 'Duration',
  set_number: 'Set number',
  setNumber: 'Set number',
  notes: 'Notes',
}

/**
 * Turn a FastAPI 422 body into one sentence a person can act on.
 *
 * FastAPI answers a failed Pydantic check with
 *   [{ "loc": ["body","weight"], "msg": "Input should be less than or equal
 *      to 2000", "input": 5000 }]
 * which we used to `JSON.stringify` straight into a toast. That is unreadable,
 * so in practice nobody could tell WHICH field was rejected — the Sentry issue
 * that prompted this had 145 events and named no field at all.
 *
 * Returns null when the shape isn't a validation array, so callers fall back to
 * whatever detail the server sent.
 */
export function formatValidationDetail(detail: unknown): string | null {
  if (!Array.isArray(detail) || detail.length === 0) return null
  const parts: string[] = []
  for (const item of detail) {
    if (!item || typeof item !== 'object') continue
    const { loc, msg, input } = item as { loc?: unknown[]; msg?: string; input?: unknown }
    if (typeof msg !== 'string') continue
    const raw = Array.isArray(loc) && loc.length ? String(loc[loc.length - 1]) : ''
    const label =
      FIELD_LABELS[raw] ??
      // camelCase / snake_case → "Set number"
      // Sentence case, matching the labels above ("Set number", not "Set
      // Number") so a labelled and an unlabelled field read the same way.
      (raw
        ? raw
            .replace(/_/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .toLowerCase()
            .replace(/^./, (c) => c.toUpperCase())
        : 'That value')
    // Pydantic phrases everything as "Input should be …"; "must be" reads like
    // an instruction rather than a parser complaint.
    const rule = msg.replace(/^Input should be/i, 'must be').replace(/^Value error, /i, '')
    const shown = input === undefined || input === null || input === '' ? '' : ` (you entered ${String(input)})`
    parts.push(`${label} ${rule}${shown}`)
  }
  return parts.length ? parts.join('. ') : null
}

/**
 * Throw an error if the response is not ok (status 2xx).
 * Extracts the error detail from the JSON body when available.
 */
export async function throwIfNotOk(res: Response): Promise<void> {
  if (res.ok) return
  let detail = res.statusText
  let code: string | undefined
  try {
    const body = await res.clone().json()
    if (body?.detail) {
      // Object details (e.g. the profile gate's {code, missing, message})
      // carry their human sentence in .message — surface that, never the
      // raw JSON blob.
      if (typeof body.detail === 'string') detail = body.detail
      else if (typeof body.detail?.message === 'string') {
        detail = body.detail.message
        if (typeof body.detail.code === 'string') code = body.detail.code
      } else detail = formatValidationDetail(body.detail) ?? JSON.stringify(body.detail)
    } else if (body?.message) detail = body.message
  } catch {
    // body wasn't JSON – keep statusText
  }
  const err = new Error(`${res.status}: ${detail}`)
  if (code) (err as Error & { code?: string }).code = code
  throw err
}

export async function apiFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const buildHeaders = async (): Promise<Record<string, string>> => {
    const token = await getFreshAccessToken()
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    }
    if (token) headers['Authorization'] = `Bearer ${token}`
    return headers
  }

  let response = await fetch(`${BASE_URL}${path}`, { ...options, headers: await buildHeaders() })
  if (response.status === 401) {
    // Same race-tolerant retry as the axios interceptor — refresh once and
    // try again before falling through to handle401's sign-out path.
    const outcome = await tryRefreshSession()
    if (outcome === 'ok') {
      response = await fetch(`${BASE_URL}${path}`, { ...options, headers: await buildHeaders() })
    }
    if (response.status === 401) {
      await handle401(outcome)
    }
  }
  return response
}

/**
 * One request with a token the caller already holds — no refresh, no retry,
 * and no 401 → sign-out handling, aborted after `timeoutMs`. For the calls
 * made WHILE signing out (lib/push.ts unregisterPushBeforeSignOut): going
 * through apiFetch there would refresh on a 401 and could re-enter signOut.
 * Everything else uses apiFetch.
 */
export async function apiFetchWithToken(
  path: string,
  token: string,
  options: RequestInit = {},
  timeoutMs = 2500,
): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(`${BASE_URL}${path}`, {
      ...options,
      signal: controller.signal,
      headers: { ...(options.headers as Record<string, string>), Authorization: `Bearer ${token}` },
    })
  } finally {
    clearTimeout(timer)
  }
}

/**
 * When a `fetch` never reaches the server — the device is offline, DNS fails,
 * or the backend is restarting mid-deploy — the browser rejects with a raw,
 * user-hostile string: WebKit/iOS says "Load failed", Chrome "Failed to fetch".
 * Surfacing that verbatim makes a 15-second deploy blip look like a broken
 * feature. Map those network-level failures to a plain, actionable message;
 * pass real server-provided messages (e.g. a 400's `detail`) straight through.
 */
export function friendlyFetchError(e: unknown, fallback = "Something went wrong. Please try again."): string {
  const msg = e instanceof Error ? e.message : ""
  const isNetwork =
    e instanceof TypeError ||
    /load failed|failed to fetch|network\s*error|network request failed/i.test(msg)
  if (isNetwork) return "Couldn't reach the server — check your connection and try again."
  return msg || fallback
}

/**
 * Thrown by `apiFetchWithTimeout` when the request exceeds the deadline.
 * Callers can match `err instanceof NetworkTimeoutError` instead of doing
 * brittle `err.message === "network-timeout"` string comparisons. Importantly,
 * a NetworkTimeoutError means the underlying fetch was ALSO aborted
 * (AbortController.abort()), so the request will not silently land at the
 * server after the client gave up — that asymmetry caused real duplicate
 * writes in the previous Promise.race(setTimeout(reject)) pattern.
 */
export class NetworkTimeoutError extends Error {
  readonly isNetworkTimeout = true
  constructor(timeoutMs: number) {
    super(`network request timed out after ${timeoutMs}ms`)
    this.name = 'NetworkTimeoutError'
  }
}

/**
 * Thrown by `apiFetchWithTimeout` when the caller's externalSignal is
 * aborted. Distinct from NetworkTimeoutError so callers can recognise
 * "intentional cancellation by the parent" (don't surface as a hard error;
 * the queued write-ahead copy is still in place) vs "we ran out of time".
 */
export class ExternalAbortError extends Error {
  readonly isExternalAbort = true
  constructor() {
    super('request cancelled by external signal')
    this.name = 'ExternalAbortError'
  }
}

/**
 * apiFetch with a hard deadline. The underlying fetch is aborted via
 * AbortController when the timer fires, so a request that would otherwise
 * succeed slowly is genuinely cancelled — no more duplicate-write races
 * where the client believes the request failed but the server still
 * processed it.
 */
export async function apiFetchWithTimeout(
  path: string,
  options: RequestInit & { timeoutMs?: number } = {}
): Promise<Response> {
  const { timeoutMs = 12000, signal: externalSignal, ...rest } = options
  const controller = new AbortController()
  // If we have an external signal, mirror its aborted state into our local
  // controller AND remember the listener so we can detach it in `finally`.
  // The previous `{ once: true }` version only auto-removed when the signal
  // actually fired — on normal completion the listener stayed attached and
  // closure-captured our (now-defunct) controller, leaking per request.
  let externalAborted = externalSignal?.aborted ?? false
  const externalHandler = externalSignal
    ? () => {
        externalAborted = true
        controller.abort()
      }
    : null
  if (externalSignal && !externalAborted && externalHandler) {
    externalSignal.addEventListener('abort', externalHandler)
  } else if (externalAborted) {
    controller.abort()
  }
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)
  try {
    return await apiFetch(path, { ...rest, signal: controller.signal })
  } catch (err) {
    // Reason ordering matters: timeout > external abort > the raw error.
    // Otherwise an unmount-triggered external abort would be reported as a
    // timeout (or vice versa) depending on which fired first by ms.
    if (timedOut) throw new NetworkTimeoutError(timeoutMs)
    if (externalAborted) throw new ExternalAbortError()
    throw err
  } finally {
    clearTimeout(timer)
    if (externalSignal && externalHandler) {
      externalSignal.removeEventListener('abort', externalHandler)
    }
  }
}

/**
 * Method/url/body-style wrapper of apiFetchWithTimeout, mirroring `apiRequest`
 * in shape so call sites that already use that legacy helper can swap in one
 * line. Used by the workout-start / quick-log mutations whose previous
 * hand-rolled Promise.race did not actually abort the fetch.
 */
export async function apiRequestWithTimeout(
  method: string,
  url: string,
  data?: unknown,
  timeoutMs = 12000,
): Promise<Response> {
  return apiFetchWithTimeout(url, {
    method,
    headers: data ? { 'Content-Type': 'application/json' } : undefined,
    body: data ? JSON.stringify(data) : undefined,
    timeoutMs,
  })
}
